import React, { useState, useRef, useEffect } from 'react';
import { api } from '../api/client';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  sources?: string[];
}

const SUGGESTED_QUESTIONS = [
  'What are the key steps in acquisition planning?',
  'Explain the difference between FFP and T&M contracts',
  'What FAR clauses apply to commercial item acquisitions?',
  'How do I conduct market research?',
  'What is the process for a sole source justification?',
  'Explain the Uniform Contract Format sections',
  'What are the rules on procurement integrity and ethics?',
  'How does the debriefing process work?',
];

export default function Training() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleAsk = async (question: string) => {
    if (!question.trim() || loading) return;
    const userMsg: ChatMessage = { role: 'user', content: question };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const res = await api.post<any>('/training/ask', { question });
      const answer = res.answer || res.response || 'No answer available.';
      const sources = res.sources || [];
      setMessages(prev => [...prev, { role: 'assistant', content: answer, sources }]);
    } catch (err: any) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'The training assistant is unavailable right now. Please try again.' }]);
    }
    setLoading(false);
  };

  return (
    <div style={{ maxWidth: 900 }}>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: 20, margin: 0 }}>Acquisition Workforce Development & Training</h2>
        <p style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>
          Ask questions about federal acquisition — FAR/DFARS, appropriations law, contract types, market research, ethics, and procurement best practices. Answers are grounded in the FTI training knowledge base.
        </p>
      </div>

      {/* Chat window */}
      <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, display: 'flex', flexDirection: 'column', height: 'calc(100vh - 260px)', minHeight: 400 }}>
        {/* Messages */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
          {messages.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>🎓</div>
              <h3 style={{ fontSize: 15, color: '#374151', marginBottom: 6 }}>Acquisition Training Assistant</h3>
              <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 20 }}>Ask any question about federal acquisition and contracting.</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', maxWidth: 700, margin: '0 auto' }}>
                {SUGGESTED_QUESTIONS.map(q => (
                  <button
                    key={q}
                    onClick={() => handleAsk(q)}
                    style={{ fontSize: 12, padding: '8px 14px', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 16, cursor: 'pointer', color: '#374151' }}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {messages.map((msg, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
                  <div style={{
                    maxWidth: '80%',
                    padding: '12px 16px',
                    borderRadius: msg.role === 'user' ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                    background: msg.role === 'user' ? '#4f46e5' : '#f3f4f6',
                    color: msg.role === 'user' ? 'white' : '#1f2937',
                    fontSize: 13,
                    lineHeight: 1.7,
                    whiteSpace: 'pre-wrap',
                  }}>
                    {msg.content}
                    {msg.sources && msg.sources.length > 0 && (
                      <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid #d1d5db', fontSize: 11, color: '#6b7280' }}>
                        <strong>Sources:</strong> {msg.sources.join(', ')}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {loading && (
                <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <div style={{ padding: '12px 16px', borderRadius: '12px 12px 12px 2px', background: '#f3f4f6', fontSize: 13, color: '#6b7280' }}>
                    Searching the knowledge base...
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
          )}
        </div>

        {/* Input */}
        <form
          onSubmit={(e) => { e.preventDefault(); handleAsk(input); }}
          style={{ display: 'flex', gap: 8, padding: 16, borderTop: '1px solid #e5e7eb' }}
        >
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder="Ask about acquisition, FAR clauses, contract types, ethics..."
            disabled={loading}
            style={{ flex: 1, padding: '12px 16px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13 }}
          />
          <button
            type="submit"
            disabled={loading || !input.trim()}
            style={{ padding: '12px 24px', background: loading || !input.trim() ? '#93c5fd' : '#4f46e5', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: loading || !input.trim() ? 'default' : 'pointer' }}
          >
            {loading ? 'Sending...' : 'Ask'}
          </button>
        </form>
      </div>
    </div>
  );
}
